import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const register = async (email, password, name, institution_name, institution_address, institution_phone) => {
    const { data } = await axios.post(
      `${API}/auth/register`,
      { email, password, name, institution_name, institution_address, institution_phone },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  const markManualSeen = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/manual-seen`, {}, { withCredentials: true });
      setUser((prev) => (prev ? { ...prev, has_seen_manual: true } : prev));
    } catch (error) {
      // non-blocking
    }
  }, [API]);

  const value = useMemo(
    () => ({ user, loading, register, login, logout, checkAuth, markManualSeen }),
    [user, loading, markManualSeen]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
